import { useState, type JSX, type ReactNode } from 'react'
import { useNavigate } from 'react-router'
import { Ticket, ChevronDown, ChevronRight, ExternalLink } from 'lucide-react'
import {
  isResolvedStatus,
  statusChipClass,
  statusLabel,
  type ConversationRef,
} from './ticketGrouping.js'

interface Props {
  conversation: ConversationRef | undefined
  conversationId: string
  messageCount: number
  /** Brand accent for the owning channel (WhatsApp green, Facebook blue). */
  accentClassName?: string
  children: ReactNode
}

/**
 * A collapsible block wrapping the messages that were promoted onto one conversation,
 * with its reference, current status, and a jump to the conversation detail.
 * Resolved conversations start collapsed so the live part of the thread stays visible.
 */
export function TicketGroupBlock({
  conversation,
  conversationId,
  messageCount,
  accentClassName = 'text-[#128C7E]',
  children,
}: Props): JSX.Element {
  const navigate = useNavigate()
  const resolved = conversation ? isResolvedStatus(conversation.status) : false
  const [open, setOpen] = useState(!resolved)

  const reference = conversation?.reference_id ?? conversationId.slice(0, 8)
  const status = conversation?.status ?? 'open'

  return (
    <div className="my-2 overflow-hidden rounded-lg border border-gray-200 bg-white/70">
      <div className="flex items-center gap-2 border-b border-gray-100 bg-gray-50/80 px-3 py-2">
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
          aria-expanded={open}
        >
          {open ? (
            <ChevronDown className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
          )}
          <Ticket className={`h-3.5 w-3.5 flex-shrink-0 ${accentClassName}`} />
          <span className="truncate text-xs font-semibold text-gray-700">{reference}</span>
          <span
            className={`flex-shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${statusChipClass(status)}`}
          >
            {statusLabel(status)}
          </span>
          <span className="flex-shrink-0 text-[10px] text-gray-400">
            {messageCount} message{messageCount === 1 ? '' : 's'}
          </span>
        </button>
        <button
          onClick={() => navigate(`/agent/conversation/view/${conversationId}`)}
          className="flex flex-shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
          title="Open the conversation"
        >
          View
          <ExternalLink className="h-3 w-3" />
        </button>
      </div>
      {open && <div className="flex flex-col gap-1.5 px-3 py-2">{children}</div>}
    </div>
  )
}
